import React from "react";
import { Button, Tag } from "antd";
import {
    PlayCircleOutlined,
    FilePdfOutlined,
    LinkOutlined,
} from "@ant-design/icons";
import LottieIcon from "../../components/shared/lottieIcon/LottieIcon";
import CustomPopover from "../../components/shared/custompopover/CustomPopover";
import editIcon from "../../assets/icons/Edit.svg";
import deleteIcon from "../../assets/icons/Delete.svg";
import optionsIcon from "../../assets/icons/Options.svg";
import viewIcon from "../../assets/icons/Eye.svg";
import { formatDate } from "../../utility/formatDate";

const getResourceType = (resourceLink) => {
    if (!resourceLink) return "unknown";
    const lower = resourceLink.toLowerCase();
    if (lower.includes("youtube.com") || lower.includes("youtu.be")) return "youtube";
    if (lower.endsWith(".mp4") || lower.endsWith(".mov")) return "video";
    if (lower.endsWith(".pdf")) return "pdf";
    if (lower.startsWith("http://") || lower.startsWith("https://")) return "website";
    return "unknown";
};

const STATUS_STYLES = {
    Active: {
        color: "rgba(8, 138, 21, 1)",
        background: "rgba(238, 255, 240, 1)",
        borderColor: "rgba(149, 222, 157, 1)",
    },
    Inactive: {
        color: "rgba(200, 0, 0, 1)",
        background: "rgba(255, 238, 238, 1)",
        borderColor: "rgba(255, 170, 170, 1)",
    },
};

const renderStatusTag = (val) => {
    const displayVal = val === true || val === "Active" ? "Active" : "Inactive";
    const style = STATUS_STYLES[displayVal];
    return (
        <Tag
            style={{
                color: style.color,
                backgroundColor: style.background,
                borderColor: style.borderColor,
                padding: "10px 16px",
                borderRadius: "10px",
                border: "1px solid",
                fontWeight: "400",
                fontSize: "14px",
            }}
        >
            {displayVal}
        </Tag>
    );
};


const renderActionButton = (resourceLink, record, setSelectedRecord, setShowContent) => {
    const resourceType = getResourceType(resourceLink);

    const buttonStyle = {
        borderColor: "#910E11",
        color: "#910E11",
        borderRadius: "6px",
        fontWeight: 500,
        width: "120px",
    };

    switch (resourceType) {
        case "youtube":
            return (
                <Button
                    style={buttonStyle}
                    onClick={() => {
                        setSelectedRecord(record);
                        setShowContent(true);
                    }}
                >
                    YouTube Link
                </Button>
            );

        case "video":
            return (
                <Button
                    style={buttonStyle}
                    onClick={() => {
                        setSelectedRecord(record);
                        setShowContent(true);
                    }}
                >
                    Video
                </Button>
            );

        case "pdf":
            return (
                <Button
                    style={buttonStyle}
                    onClick={() => {
                        setSelectedRecord(record);
                        setShowContent(true);
                    }}
                >
                    PDF
                </Button>
            );
        case "website":
            return (
                <Button
                    style={buttonStyle}
                    onClick={() => window.open(record.resourceLink, "_blank")}
                >
                    Website
                </Button>
            );
        default:
            return <span>Unsupported</span>;
    }
};

export const getCourseContentColumns = ({
    navigate,
    handleEdit,
    handleDelete,
    setShowContent,
    setSelectedRecord,
    visiblePopover,
    setVisiblePopover,
    hoveredButton,
    setHoveredButton,
}) => [
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: 200,
            ellipsis: true,
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            width: 200,
            ellipsis: true,
        },
        {
            title: "Content Type",
            dataIndex: "contentType",
            key: "contentType",
            width: 170,
        },
        {
            title: <div style={{ textAlign: "left" }}>Status</div>,
            dataIndex: "active",
            width: 170,
            render: renderStatusTag,
        },
        {
            title: "Resource",
            dataIndex: "resourceLink",
            key: "resourceLink",
            width: 170,
            render: (_, record) =>
                record.resourceLink
                    ? renderActionButton(record.resourceLink, record, setSelectedRecord, setShowContent)
                    : "N/A",
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 150,
            render: (date) => formatDate(date, "DD-MM-YYYY"),
        },
        {
            title: "Actions",
            key: "actions",
            width: 80,
            fixed: "right",
            render: (_, record) => {
                const popoverContent = (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <Button
                            className="navbar-item"
                            onClick={(e) => {
                                console.log({ record });
                                navigate(
                                    `/books/course-content/${record.courseId}/single-report/${record.courseContentId}`
                                );;
                            }}
                            onMouseEnter={() => setHoveredButton("report")}
                            onMouseLeave={() => setHoveredButton(null)}
                            style={{ width: "11rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        >
                            <span>Report</span>
                            {hoveredButton === "report" ? (
                                <LottieIcon name="viewIcon" size={24} shouldPlay={true} />
                            ) : (
                                <img src={viewIcon} alt="report-icon" height={20} />
                            )}
                        </Button>
                        <Button
                            className="navbar-item"
                            onClick={() => { handleEdit(record); setVisiblePopover(null); }}
                            onMouseEnter={() => setHoveredButton("edit")}
                            onMouseLeave={() => setHoveredButton(null)}
                            style={{ width: "11rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        >
                            <span>Edit</span>
                            {hoveredButton === "edit"
                                ? <LottieIcon name="editIcon" size={24} shouldPlay />
                                : <img src={editIcon} alt="edit" height={20} />
                            }
                        </Button>

                        <Button
                            className="navbar-item"
                            onClick={() => { handleDelete(record); setVisiblePopover(null); }}
                            onMouseEnter={() => setHoveredButton("delete")}
                            onMouseLeave={() => setHoveredButton(null)}
                            style={{ width: "11rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        >
                            <span>Delete</span>
                            {hoveredButton === "delete"
                                ? <LottieIcon name="trashIcon" size={24} shouldPlay />
                                : <img src={deleteIcon} alt="delete" height={20} />
                            }
                        </Button>
                    </div>
                );

                return (
                    <CustomPopover
                        content={popoverContent}
                        trigger="click"
                        className="admin-popover"
                        placement="bottomRight"
                        visible={visiblePopover === record.courseContentId}
                        onVisibleChange={(visible) => {
                            setVisiblePopover(visible ? record.courseContentId : null);
                            if (!visible) setHoveredButton(null);
                        }}
                        getPopupContainer={() => document.body}
                    >
                        <img src={optionsIcon} alt="options" height={24} />
                    </CustomPopover>
                );
            },
        },
    ];